import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import { changesApi } from '../../../services/changesApi';
import type { ChangeExtraFields, ChangeFormField, ChangeRequest } from '../../../types/changes';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  buildExtraFieldsPayloadFromNatives,
  hydrateExtraFields,
  validateExtraFieldsFromNatives,
} from '../changeFormSchema';
import { canEditPostImplementation } from '../changeLabels';
import ChangePostImplConfigurableForm, {
  postImplNativeMap,
  type PostImplFormValues,
} from './ChangePostImplConfigurableForm';

interface Props {
  change: ChangeRequest;
  onUpdated: () => Promise<void> | void;
}

const yesNo = (value: boolean | null | undefined) =>
  value === true ? 'yes' : value === false ? 'no' : '';

const ChangeImplantationForm: React.FC<Props> = ({ change, onUpdated }) => {
  const { showConfirm } = useModal();
  const readOnly = change.status !== 'revisar' || !canEditPostImplementation(change);
  const [rollback, setRollback] = useState(yesNo(change.rollback_executed));
  const [incidents, setIncidents] = useState(yesNo(change.had_incidents));
  const [executed, setExecuted] = useState(yesNo(change.change_executed));
  const [success, setSuccess] = useState(yesNo(change.change_executed_successfully));
  const [observation, setObservation] = useState(change.implementation_result || '');
  const [schema, setSchema] = useState<ChangeFormField[]>([]);
  const [extras, setExtras] = useState<ChangeExtraFields>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void changesApi
      .getPostImplFormSchema()
      .then((data) => {
        if (cancelled) return;
        const items = data.items || [];
        setSchema(items);
        setExtras(hydrateExtraFields(items, change.post_impl_extra_fields));
      })
      .catch(() => {
        if (!cancelled) setSchema([]);
      });
    return () => {
      cancelled = true;
    };
  }, [change.id]);

  const form: PostImplFormValues = {
    rollback_executed: rollback,
    had_incidents: incidents,
    change_executed: executed,
    change_executed_successfully: success,
    observation,
  };
  const useConfigurableForm = schema.some((field) => field.kind === 'native');

  const handleSave = async () => {
    if (useConfigurableForm) {
      const natives = postImplNativeMap(form);
      const missingNative = schema.find((field) => {
        if (field.kind !== 'native' || !field.is_required || !field.is_visible) return false;
        const value = natives[field.native_key || ''] || '';
        return !value.trim();
      });
      if (missingNative) {
        setError(`Preencha o campo obrigatório: ${missingNative.label}.`);
        return;
      }
      const extraError = validateExtraFieldsFromNatives(schema, natives, extras);
      if (extraError) {
        setError(extraError);
        return;
      }
    } else if (!rollback || !incidents || !executed || !success) {
      setError('Responda os quatro campos de sim/não. Observação é opcional.');
      return;
    }
    const ok = await showConfirm({
      title: 'Salvar pós-implantação',
      message: 'Tem certeza que deseja salvar o registro pós-implantação?',
      confirmLabel: 'Salvar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const extraPayload = buildExtraFieldsPayloadFromNatives(
        schema,
        postImplNativeMap(form),
        extras,
      );
      const body: Parameters<typeof changesApi.postImplementation>[1] = {
        rollback_executed: rollback === 'yes',
        had_incidents: incidents === 'yes',
        change_executed: executed === 'yes',
        change_executed_successfully: success === 'yes',
        observation: observation.trim() || null,
      };
      if (Object.keys(extraPayload).length) {
        body.extra_fields = extraPayload;
      }
      await changesApi.postImplementation(change.id, body);
      setMessage('Pós-implantação registrado.');
      await onUpdated();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao salvar pós-implantação.'));
    } finally {
      setSaving(false);
    }
  };

  const applyFormPatch = (patch: Partial<PostImplFormValues>) => {
    if (patch.rollback_executed !== undefined) setRollback(patch.rollback_executed);
    if (patch.had_incidents !== undefined) setIncidents(patch.had_incidents);
    if (patch.change_executed !== undefined) setExecuted(patch.change_executed);
    if (patch.change_executed_successfully !== undefined) {
      setSuccess(patch.change_executed_successfully);
    }
    if (patch.observation !== undefined) setObservation(patch.observation);
  };

  return (
    <Card className="mb-3">
      <Card.Header>Registro pós-implantação</Card.Header>
      <Card.Body>
        {readOnly ? (
          <Alert variant="light" className="py-2">
            {change.status !== 'revisar'
              ? 'Encerrada: este bloco fica só para histórico.'
              : 'Somente administrador, gestor de mudanças, dono ou solicitante podem editar; você está em modo leitura.'}
          </Alert>
        ) : null}
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {message ? (
          <Alert variant="success" dismissible onClose={() => setMessage(null)}>
            {message}
          </Alert>
        ) : null}
        {useConfigurableForm ? (
          <ChangePostImplConfigurableForm
            fields={schema}
            form={form}
            extras={extras}
            onFormChange={applyFormPatch}
            onExtraChange={(fieldId, value) =>
              setExtras((prev) => ({ ...prev, [String(fieldId)]: value }))
            }
            readOnly={readOnly}
          />
        ) : (
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Rollback executado?</Form.Label>
              <Form.Select value={rollback} disabled={readOnly} onChange={(e) => setRollback(e.target.value)}>
                <option value="">Selecione</option>
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Houve incidentes?</Form.Label>
              <Form.Select value={incidents} disabled={readOnly} onChange={(e) => setIncidents(e.target.value)}>
                <option value="">Selecione</option>
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>A mudança foi executada?</Form.Label>
              <Form.Select value={executed} disabled={readOnly} onChange={(e) => setExecuted(e.target.value)}>
                <option value="">Selecione</option>
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label>Mudança foi executada com sucesso?</Form.Label>
              <Form.Select value={success} disabled={readOnly} onChange={(e) => setSuccess(e.target.value)}>
                <option value="">Selecione</option>
                <option value="yes">Sim</option>
                <option value="no">Não</option>
              </Form.Select>
            </Col>
            <Col md={12}>
              <Form.Label>Observação (opcional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={observation}
                disabled={readOnly}
                onChange={(e) => setObservation(e.target.value)}
              />
            </Col>
          </Row>
        )}
        {readOnly ? null : (
          <div className="mt-3">
            <Button disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Salvando...' : 'Salvar pós-implantação'}
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ChangeImplantationForm;
